import React, { useState, useEffect } from 'react';
import { Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import ApiService from '../../helpers/apiService';
import { v4 as uuidv4 } from 'uuid';

interface Limit {
  id: number;
  code: string;
  object_type: number;
  action_type: number;
  object_id: string;
  limit_type: number;
  value: string;
  remaining: string;
  duration: number;
  started_at: string;
  description: string;
}

const OBJECT_TYPES: Record<number, string> = { 0: 'Пользователь', 1: 'Счёт', 2: 'Карта' };
const ACTION_TYPES: Record<number, string> = { 0: 'Общий', 1: 'Пополнение', 2: 'Вывод', 3: 'Платежи картой', 4: 'Перевод', 5: 'Получение' };
const LIMIT_TYPES: Record<number, string> = { 0: 'Макс. сумма', 1: 'Макс. кол-во', 2: 'Макс. за операцию', 3: 'Мин. за операцию' };
const DURATIONS: Record<number, string> = { 86400: 'День', 604800: 'Неделя', 2592000: 'Месяц' };

const Limits: React.FC = () => {
  const [limitsData, setLimitsData] = useState<Limit[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<'add' | 'edit'>('add');
  const [currentLimit, setCurrentLimit] = useState<Limit | null>(null);

  const fetchLimits = async () => {
    setLoading(true);
    try {
      const data = await ApiService.getLimits<{ data: Limit[] }>({}, uuidv4());
      setLimitsData(data.data);
    } catch (e) {
      console.error('Ошибка при получении лимитов:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLimits(); }, []);

  const handleAddLimit = () => { setModalType('add'); setCurrentLimit(null); setShowModal(true); };
  const handleEditLimit = (limit: Limit) => { setModalType('edit'); setCurrentLimit(limit); setShowModal(true); };

  const handleDeleteLimit = async (code: string) => {
    if (!window.confirm('Удалить лимит?')) return;
    try {
      await ApiService.deleteLimit(code, uuidv4());
      fetchLimits();
    } catch (e) {
      console.error('Ошибка при удалении лимита:', e);
    }
  };

  const handleModalSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const limitData = Object.fromEntries(formData.entries());
    const formattedLimitData: any = {
      ...limitData,
      object_type: parseInt(limitData.object_type as string, 10),
      action_type: parseInt(limitData.action_type as string, 10),
      limit_type: parseInt(limitData.limit_type as string, 10),
      duration: parseInt(limitData.duration as string, 10),
      started_at: limitData.started_at ? new Date(limitData.started_at as string).toISOString() : new Date().toISOString(),
      description: limitData.description || '',
      object_id: ''
    };
    if (formattedLimitData.limit_type < 0 || formattedLimitData.duration < 0 || formattedLimitData.object_type < 0 || formattedLimitData.action_type < 0) {
      alert('Значения не могут быть отрицательными');
      return;
    }
    if (modalType === 'edit' && currentLimit) formattedLimitData.id = currentLimit.id;
    try {
      if (modalType === 'add') await ApiService.addLimit(formattedLimitData, uuidv4());
      else if (modalType === 'edit') await ApiService.updateLimit(formattedLimitData, uuidv4());
      fetchLimits();
      setShowModal(false);
    } catch (e) {
      console.error('Ошибка при сохранении лимита:', e);
    }
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h3 className="mb-0">Лимиты</h3>
              <p className="mt-1 mb-0 text-muted small">Управление лимитами операций</p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={fetchLimits} disabled={loading}>
                Обновить
              </Button>
              <Button variant="primary" size="sm" onClick={handleAddLimit}>
                + Добавить лимит
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="d-flex align-items-center gap-2 p-4">
              <Spinner animation="border" size="sm" /><span>Загрузка...</span>
            </div>
          ) : (
            <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Идентификатор</th>
                  <th>Тип объекта</th>
                  <th>Тип действия</th>
                  <th>ID объекта</th>
                  <th>Тип лимита</th>
                  <th>Значение</th>
                  <th>Остаток</th>
                  <th>Период</th>
                  <th>Начало</th>
                  <th>Описание</th>
                  <th style={{ width: 100 }}>Управление</th>
                </tr>
              </thead>
              <tbody>
                {limitsData.length === 0 ? (
                  <tr><td colSpan={12} className="text-muted text-center py-3">Нет данных</td></tr>
                ) : limitsData.map(limit => (
                  <tr key={limit.id}>
                    <td>{limit.id}</td>
                    <td><code style={{ fontSize: '0.75rem' }}>{limit.code}</code></td>
                    <td>
                      <Badge bg="light" text="dark" style={{ fontSize: '0.7rem' }}>
                        {OBJECT_TYPES[limit.object_type] ?? limit.object_type}
                      </Badge>
                    </td>
                    <td>{ACTION_TYPES[limit.action_type] ?? limit.action_type}</td>
                    <td className="text-truncate" style={{ maxWidth: 120 }}>{limit.object_id || '—'}</td>
                    <td>{LIMIT_TYPES[limit.limit_type] ?? limit.limit_type}</td>
                    <td>{limit.value}</td>
                    <td>{limit.remaining || '—'}</td>
                    <td>{DURATIONS[limit.duration] ?? limit.duration}</td>
                    <td>{new Date(limit.started_at).toLocaleString()}</td>
                    <td className="text-truncate" style={{ maxWidth: 160 }} title={limit.description}>
                      {limit.description || '—'}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button variant="outline-secondary" size="sm" style={{ fontSize: '0.7rem', padding: '2px 6px' }} onClick={() => handleEditLimit(limit)}>
                          Ред.
                        </Button>
                        <Button variant="outline-danger" size="sm" style={{ fontSize: '0.7rem', padding: '2px 6px' }} onClick={() => handleDeleteLimit(limit.code)}>
                          Del
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Form onSubmit={handleModalSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{modalType === 'add' ? 'Добавить лимит' : 'Редактировать лимит'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12">
                <Form.Label className="small">Идентификатор <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="text" name="code" defaultValue={currentLimit?.code || ''} required />
              </div>
              <div className="col-6">
                <Form.Label className="small">Тип объекта</Form.Label>
                <Form.Select size="sm" name="object_type" defaultValue={currentLimit?.object_type.toString() || '0'}>
                  <option value="0">Пользователь</option>
                  <option value="1">Счёт</option>
                  <option value="2">Карта</option>
                </Form.Select>
              </div>
              <div className="col-6">
                <Form.Label className="small">Тип действия</Form.Label>
                <Form.Select size="sm" name="action_type" defaultValue={currentLimit?.action_type.toString() || '0'}>
                  <option value="0">Общий</option>
                  <option value="1">Пополнение</option>
                  <option value="2">Вывод</option>
                  <option value="3">Платежи картой</option>
                  <option value="4">Перевод</option>
                  <option value="5">Получение</option>
                </Form.Select>
              </div>
              <div className="col-6">
                <Form.Label className="small">Тип лимита</Form.Label>
                <Form.Select size="sm" name="limit_type" defaultValue={currentLimit?.limit_type.toString() || '0'}>
                  <option value="0">Макс. сумма</option>
                  <option value="1">Макс. кол-во</option>
                  <option value="2">Макс. за операцию</option>
                  <option value="3">Мин. за операцию</option>
                </Form.Select>
              </div>
              <div className="col-6">
                <Form.Label className="small">Период</Form.Label>
                <Form.Select size="sm" name="duration" defaultValue={currentLimit?.duration.toString() || '86400'}>
                  <option value="86400">День</option>
                  <option value="604800">Неделя</option>
                  <option value="2592000">Месяц</option>
                </Form.Select>
              </div>
              <div className="col-6">
                <Form.Label className="small">Значение <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="text" name="value" defaultValue={currentLimit?.value || ''} required />
              </div>
              <div className="col-6">
                <Form.Label className="small">Начало действия</Form.Label>
                <Form.Control
                  size="sm"
                  type="datetime-local"
                  name="started_at"
                  defaultValue={currentLimit ? new Date(currentLimit.started_at).toISOString().slice(0, -1) : ''}
                />
              </div>
              <div className="col-12">
                <Form.Label className="small">Описание</Form.Label>
                <Form.Control size="sm" as="textarea" rows={2} name="description" defaultValue={currentLimit?.description || ''} />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" size="sm" onClick={() => setShowModal(false)}>Отмена</Button>
            <Button variant="primary" size="sm" type="submit">Сохранить</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default Limits;
